'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { openDialog } from '@/app/components/shared/imperative-dialog';
import { safeStorage } from '@/app/utils/client';
import { type ConsentType, CreationConsentDialog } from '../_components/CreationConsentDialog';

const CREATION_CONSENT_STORAGE_KEY = 'creation_sensitive_info_consent_v2';

interface CreationConsentConfig {
  requireVoiceConsent?: boolean;
  requireFaceConsent?: boolean;
  requireReferenceAudioConsent?: boolean;
}

function getCreationConsentStorageKey(userId: string, consentType: ConsentType): string {
  return `${CREATION_CONSENT_STORAGE_KEY}_${consentType}_${userId}`;
}

function getRequiredConsentTypes(config: CreationConsentConfig): ConsentType[] {
  return [
    ...(config.requireVoiceConsent ? (['voice'] as const) : []),
    ...(config.requireFaceConsent ? (['face'] as const) : []),
    ...(config.requireReferenceAudioConsent ? (['referenceAudio'] as const) : []),
  ];
}

export function useCreationConsent(userId: string | undefined, config: CreationConsentConfig) {
  const t = useTranslations('create.creationConsent');

  return useCallback(async (): Promise<boolean> => {
    const requiredConsentTypes = getRequiredConsentTypes(config);

    if (!userId || requiredConsentTypes.length === 0) {
      return true;
    }

    const pendingConsentTypes = requiredConsentTypes.filter(
      (consentType) => safeStorage.getItem(getCreationConsentStorageKey(userId, consentType)) !== 'true',
    );

    if (pendingConsentTypes.length === 0) {
      return true;
    }

    const consentContentMap: Record<ConsentType, { linkText: string; href: string }> = {
      voice: {
        linkText: t('voice.linkText'),
        href: '/consent-voice',
      },
      face: {
        linkText: t('face.linkText'),
        href: '/consent-face',
      },
      referenceAudio: {
        linkText: t('referenceAudio.linkText'),
        href: '/consent-reference-audio',
      },
    };

    return new Promise<boolean>((resolve) => {
      let isResolved = false;
      let closeDialog = () => {};

      const finish = (result: boolean) => {
        if (isResolved) {
          return;
        }
        isResolved = true;
        resolve(result);
      };

      const dialog = openDialog({
        title: t('title'),
        description: t('description'),
        className: 'w-[560px] sm:w-[calc(100%-24px)]',
        content: (
          <CreationConsentDialog
            consentTypes={pendingConsentTypes}
            dialogTip={t('tip')}
            agreePrefix={t('agreePrefix')}
            cancelText={t('cancel')}
            confirmText={t('confirm')}
            consentContentMap={consentContentMap}
            onCancel={() => {
              finish(false);
              closeDialog();
            }}
            onConfirm={(agreedConsentTypes) => {
              agreedConsentTypes.forEach((consentType) => {
                safeStorage.setItem(getCreationConsentStorageKey(userId, consentType), 'true');
              });
              finish(true);
              closeDialog();
            }}
          />
        ),
        onClose: () => {
          finish(false);
        },
      });

      closeDialog = dialog.close;
    });
  }, [config, t, userId]);
}
